/*
 * ServiceHeroDiagram
 * -------------------------------------------------------------
 * Restrained, professional, code-native animated process
 * diagrams for the hero area of each service-detail page.
 *
 * • Pure HTML / CSS / inline SVG (no bitmaps, no libraries).
 * • Subtle motion (opacity / transform / stroke-dashoffset).
 * • All animations respect prefers-reduced-motion — see
 *   /app/frontend/src/index.css (search "ep-hd-").
 *
 * One distinct metaphor per slug — never a shared template.
 * Pazaryeri uses its own dedicated OpsFlowPanel (in
 * /app/frontend/src/pages/PazaryeriDetail.jsx).
 */

/* ------------------------------------------------------------------ */
/* 1) uye-isyerine-ozel-siparis-sistemi — Web → Sipariş → Mobil       */
/* ------------------------------------------------------------------ */
function OwnChannelDiagram() {
  return (
    <div className="ep-hd" data-testid="hero-diagram-uye-isyerine-ozel-siparis-sistemi">
      <div className="ep-hd-head">
        <span className="ep-hd-head-label">İşyerinize Özel Sipariş Akışı</span>
        <span className="ep-hd-live">
          <span className="ep-hd-live-dot" />
          Canlı
        </span>
      </div>

      <div className="ep-hd-oc-grid">
        {/* Browser card */}
        <div className="ep-hd-oc-browser ep-hd-appear" style={{ animationDelay: "60ms" }}>
          <div className="ep-hd-oc-browser-bar">
            <span className="ep-hd-dot" />
            <span className="ep-hd-dot ep-hd-dot-y" />
            <span className="ep-hd-dot ep-hd-dot-g" />
            <span className="ep-hd-oc-url">emek-market.com</span>
          </div>
          <div className="ep-hd-oc-cart">
            <div className="ep-hd-oc-cart-row">
              <span className="ep-hd-oc-thumb" />
              <span className="ep-hd-oc-line ep-hd-w-60" />
            </div>
            <div className="ep-hd-oc-cart-row">
              <span className="ep-hd-oc-thumb" />
              <span className="ep-hd-oc-line ep-hd-w-45" />
            </div>
            <div className="ep-hd-oc-cta">Siparişi Tamamla</div>
            <span className="ep-hd-cursor" aria-hidden="true" />
          </div>
        </div>

        {/* Connector with travelling order pill */}
        <div className="ep-hd-oc-wire" aria-hidden="true">
          <svg viewBox="0 0 120 120" preserveAspectRatio="none">
            <path
              d="M 4 24 C 60 24, 60 96, 116 96"
              className="ep-hd-path"
              fill="none"
              stroke="rgba(0,242,46,0.55)"
              strokeWidth="1.5"
              strokeDasharray="4 4"
            />
          </svg>
          <span className="ep-hd-oc-pill">
            <svg viewBox="0 0 16 16" width="10" height="10" aria-hidden="true">
              <path d="M2 4 h9 l2 3 v4 h-2 a2 2 0 1 1 -4 0 h-3 a2 2 0 1 1 -4 0 z"
                fill="currentColor" />
            </svg>
            <span>#1048</span>
          </span>
        </div>

        {/* Phone card */}
        <div className="ep-hd-oc-phone ep-hd-appear" style={{ animationDelay: "320ms" }}>
          <div className="ep-hd-oc-phone-notch" />
          <div className="ep-hd-oc-phone-screen">
            <div className="ep-hd-oc-ping" />
            <span className="ep-hd-oc-check" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M4 12 l 5 5 l 11 -11" fill="none" stroke="#00F22E"
                  strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <div className="ep-hd-oc-phone-caption">Sipariş Alındı</div>
            <div className="ep-hd-oc-phone-sub">Hazırlanıyor</div>
          </div>
        </div>
      </div>

      <div className="ep-hd-foot">
        <span>Web → İşletme → Kurye</span>
        <span>3 adım • Tek panel</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 2) kurye-cozumu — Mağaza → Rota → Müşteri (courier moving)        */
/* ------------------------------------------------------------------ */
function CourierDiagram() {
  return (
    <div className="ep-hd" data-testid="hero-diagram-kurye-cozumu">
      <div className="ep-hd-head">
        <span className="ep-hd-head-label">Kurye Rota Takibi</span>
        <span className="ep-hd-live">
          <span className="ep-hd-live-dot" />
          Yolda
        </span>
      </div>

      <div className="ep-hd-kr-map" aria-hidden="true">
        {/* Map grid */}
        <svg viewBox="0 0 320 180" className="ep-hd-kr-svg" preserveAspectRatio="none">
          <defs>
            <pattern id="epHdGrid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none"
                stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="320" height="180" fill="url(#epHdGrid)" />

          {/* Landmark blocks */}
          <rect x="40" y="30" width="34" height="22" rx="3"
            fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.06)" />
          <rect x="180" y="24" width="46" height="30" rx="3"
            fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.06)" />
          <rect x="70" y="120" width="60" height="26" rx="3"
            fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.06)" />
          <rect x="220" y="118" width="60" height="34" rx="3"
            fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.06)" />

          {/* Route path — animated draw */}
          <path
            id="epHdCourierRoute"
            d="M 40 150 C 90 150, 100 60, 170 60 S 260 130, 290 40"
            className="ep-hd-kr-route"
            fill="none"
            stroke="rgba(0,242,46,0.7)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="6 5"
          />

          {/* Store pin */}
          <g transform="translate(34,144)">
            <circle r="6" fill="#00F22E" />
            <circle r="10" fill="none" stroke="rgba(0,242,46,0.4)" strokeWidth="1.5"
              className="ep-hd-kr-store-ring" />
          </g>

          {/* Customer pin (pulsing) */}
          <g transform="translate(290,40)">
            <circle r="4" fill="#fff" />
            <circle r="7" fill="none" stroke="rgba(0,242,46,0.9)" strokeWidth="1.6" />
            <circle r="12" fill="none" stroke="rgba(0,242,46,0.5)" strokeWidth="1.2"
              className="ep-hd-kr-customer-pulse" />
          </g>

          {/* Moving courier scooter */}
          <g className="ep-hd-kr-courier">
            <animateMotion dur="6s" repeatCount="indefinite" rotate="auto"
              keyPoints="0;0.25;0.55;0.75;1"
              keyTimes="0;0.25;0.5;0.75;1"
              calcMode="spline"
              keySplines="0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1">
              <mpath href="#epHdCourierRoute" />
            </animateMotion>
            <circle r="9" fill="rgba(0,242,46,0.14)"
              stroke="rgba(0,242,46,0.65)" strokeWidth="1.2" />
            <path d="M -3 -1 h 4 l 1 -2 h 2 l -1 3 l 1 2 h -3 z"
              fill="#00F22E" />
          </g>
        </svg>

        {/* Legend chips */}
        <div className="ep-hd-kr-chips">
          <span className="ep-hd-kr-chip"><span className="ep-hd-kr-chip-dot ep-hd-kr-chip-dot-store" />İşletme</span>
          <span className="ep-hd-kr-chip"><span className="ep-hd-kr-chip-dot ep-hd-kr-chip-dot-cust" />Müşteri</span>
        </div>
      </div>

      <div className="ep-hd-foot">
        <span>Ort. teslimat: 22 dk</span>
        <span>Canlı harita takibi</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 3) fiyat-ve-stok-entegrasyonu — ERP ⇄ Kanallar (bi-directional)   */
/* ------------------------------------------------------------------ */
function PriceStockDiagram() {
  const skus = [
    { code: "SKU-A-021", price: "₺ 129,90", stock: 42, state: "sync" },
    { code: "SKU-B-118", price: "₺ 89,50", stock: 8, state: "warn" },
    { code: "SKU-C-307", price: "₺ 249,00", stock: 74, state: "done" },
  ];
  return (
    <div className="ep-hd" data-testid="hero-diagram-fiyat-ve-stok-entegrasyonu">
      <div className="ep-hd-head">
        <span className="ep-hd-head-label">Fiyat &amp; Stok Senkron</span>
        <span className="ep-hd-live">
          <span className="ep-hd-live-dot" />
          Aktif
        </span>
      </div>

      <div className="ep-hd-ps-wrap">
        <div className="ep-hd-ps-cols">
          <div className="ep-hd-ps-col">
            <div className="ep-hd-ps-col-title">ERP / Kaynak</div>
            <div className="ep-hd-ps-badge">CANLI</div>
          </div>
          <div className="ep-hd-ps-bus" aria-hidden="true">
            <span className="ep-hd-ps-bus-line" />
            <span className="ep-hd-ps-bus-line ep-hd-ps-bus-line-2" />
            <span className="ep-hd-ps-bus-line ep-hd-ps-bus-line-3" />
          </div>
          <div className="ep-hd-ps-col ep-hd-ps-col-right">
            <div className="ep-hd-ps-col-title">Kanallar</div>
            <div className="ep-hd-ps-badge">4 kanal</div>
          </div>
        </div>

        <ul className="ep-hd-ps-list">
          {skus.map((s, i) => (
            <li key={s.code}
              className={`ep-hd-ps-row ep-hd-ps-row-${s.state}`}
              style={{ animationDelay: `${180 + i * 140}ms` }}>
              <span className="ep-hd-ps-sku">{s.code}</span>
              <span className="ep-hd-ps-arrow" aria-hidden="true">
                <span className="ep-hd-ps-arrow-pulse" />
              </span>
              <span className="ep-hd-ps-price">{s.price}</span>
              <span className="ep-hd-ps-stock">
                <em>{s.stock}</em> adet
              </span>
              <span className={`ep-hd-ps-badge-mini ep-hd-ps-badge-${s.state}`}>
                {s.state === "sync" && "eşleşiyor"}
                {s.state === "warn" && "kritik"}
                {s.state === "done" && "tamam"}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="ep-hd-foot">
        <span>Otomatik eşleme</span>
        <span>Fiyat + stok anlık</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 4) urun-ve-menu-yonetimi — Katalog hazırlığı checklist            */
/* ------------------------------------------------------------------ */
function CatalogDiagram() {
  const rows = [
    { label: "Ürün adı ve açıklama", delay: 120 },
    { label: "Kategori, marka, barkod, KDV", delay: 300 },
    { label: "Ürün görselleri", delay: 520, img: true },
    { label: "Seçenek ve ek gruplar", delay: 720 },
  ];
  return (
    <div className="ep-hd" data-testid="hero-diagram-urun-ve-menu-yonetimi">
      <div className="ep-hd-head">
        <span className="ep-hd-head-label">Katalog Hazırlığı</span>
        <span className="ep-hd-live">
          <span className="ep-hd-live-dot" />
          Yükleniyor
        </span>
      </div>

      <ul className="ep-hd-cat-list">
        {rows.map((r, i) => (
          <li key={i}
            className="ep-hd-cat-row"
            style={{ animationDelay: `${r.delay}ms` }}>
            <span className="ep-hd-cat-check" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path d="M4 12 l 5 5 l 11 -11" fill="none" stroke="currentColor"
                  strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="ep-hd-cat-label">{r.label}</span>
            {r.img ? (
              <span className="ep-hd-cat-thumbs">
                <span className="ep-hd-cat-thumb" />
                <span className="ep-hd-cat-thumb" />
                <span className="ep-hd-cat-thumb" />
              </span>
            ) : (
              <span className="ep-hd-cat-meta">
                <span className="ep-hd-cat-bar" />
              </span>
            )}
          </li>
        ))}
      </ul>

      <div className="ep-hd-cat-progress" aria-hidden="true">
        <span className="ep-hd-cat-progress-fill" />
        <span className="ep-hd-cat-progress-label">Yayın için hazırlanıyor</span>
      </div>

      <div className="ep-hd-foot">
        <span>Toplu ürün girişi</span>
        <span>Kanala hazır katalog</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 5) dijital-operasyon-yonetimi — KPI + growing bar chart           */
/* ------------------------------------------------------------------ */
function OperationsDiagram() {
  const bars = [28, 42, 36, 58, 50, 72, 84];
  return (
    <div className="ep-hd" data-testid="hero-diagram-dijital-operasyon-yonetimi">
      <div className="ep-hd-head">
        <span className="ep-hd-head-label">Operasyon Panosu</span>
        <span className="ep-hd-live">
          <span className="ep-hd-live-dot" />
          Bu hafta
        </span>
      </div>

      <div className="ep-hd-op-kpis">
        <div className="ep-hd-op-kpi ep-hd-appear" style={{ animationDelay: "80ms" }}>
          <div className="ep-hd-op-kpi-label">Sipariş</div>
          <div className="ep-hd-op-kpi-value">
            1.284 <span className="ep-hd-op-kpi-delta">+12%</span>
          </div>
        </div>
        <div className="ep-hd-op-kpi ep-hd-appear" style={{ animationDelay: "220ms" }}>
          <div className="ep-hd-op-kpi-label">Ort. sepet</div>
          <div className="ep-hd-op-kpi-value">
            ₺ 218 <span className="ep-hd-op-kpi-delta">+4%</span>
          </div>
        </div>
        <div className="ep-hd-op-kpi ep-hd-appear" style={{ animationDelay: "360ms" }}>
          <div className="ep-hd-op-kpi-label">İptal</div>
          <div className="ep-hd-op-kpi-value">
            2,1% <span className="ep-hd-op-kpi-delta ep-hd-op-kpi-delta-neg">-0,6%</span>
          </div>
        </div>
      </div>

      <div className="ep-hd-op-chart" aria-hidden="true">
        <svg viewBox="0 0 280 96" className="ep-hd-op-svg" preserveAspectRatio="none">
          {/* Grid */}
          {[24, 48, 72].map((y) => (
            <line key={y} x1="6" x2="274" y1={y} y2={y}
              stroke="rgba(255,255,255,0.05)" strokeDasharray="2 4" />
          ))}
          {/* Bars */}
          {bars.map((h, i) => {
            const x = 14 + i * 36;
            const barH = h;
            const y = 88 - barH;
            const isPeak = i === bars.length - 1;
            return (
              <rect key={i}
                x={x} y={y}
                width="20" rx="3"
                height={barH}
                fill={isPeak ? "rgba(0,242,46,0.85)" : "rgba(0,242,46,0.28)"}
                className="ep-hd-op-bar"
                style={{ transformOrigin: `${x + 10}px 88px`, animationDelay: `${i * 90 + 120}ms` }} />
            );
          })}
          {/* Trend line */}
          <path
            d="M 24 60 L 60 46 L 96 52 L 132 30 L 168 36 L 204 18 L 240 8"
            className="ep-hd-op-trend"
            fill="none"
            stroke="#00F22E"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round" />
          <circle cx="240" cy="8" r="3.5" fill="#00F22E" className="ep-hd-op-trend-dot" />
        </svg>
      </div>

      <div className="ep-hd-foot">
        <span>Kanallar arası kıyaslama</span>
        <span>Haftalık • Aylık</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

export default function ServiceHeroDiagram({ slug }) {
  switch (slug) {
    case "uye-isyerine-ozel-siparis-sistemi":
      return <OwnChannelDiagram />;
    case "kurye-cozumu":
      return <CourierDiagram />;
    case "fiyat-ve-stok-entegrasyonu":
      return <PriceStockDiagram />;
    case "urun-ve-menu-yonetimi":
      return <CatalogDiagram />;
    case "dijital-operasyon-yonetimi":
      return <OperationsDiagram />;
    default:
      return null;
  }
}
